import { lazy, useEffect, useState, Suspense } from 'react';
import {
	TextField,
	Button,
	InputAdornment,
	IconButton,
	LinearProgress,
	Grid,
	useTheme,
	Box
} from '@mui/material';
import { $loginDarkGray, $loginLightGray, authenticationPagesStyles } from 'constants/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import ErrorMessage from 'utils/errorMessages';
import { objectHasKeys } from 'utils/helpers';
import { Visibility, VisibilityOff, Apple, Android, Computer } from '@mui/icons-material';
import { redeemCheck, redeemInviteWithoutUser, redeemInviteWithUser } from 'apis/invites.api';
import { getLastItemInUrlPath } from 'utils/urlHelpers';
import { AndroidAppLink, iOSAppLink } from 'constants/generalConstants';
import AloftLogo from 'assets/images/aloft_logo_white.png';
import AirControl from 'assets/images/air_control_white.png';
import { validatePasswordShape } from 'utils/validateForm';
import { $white } from 'constants/styles/_colors';
import { useTranslation } from 'react-i18next';

const PasswordRequirementsChecklist = lazy(
	() => import('components/authentication/PasswordRequirementsChecklist')
);
const AuthenticationFooter = lazy(() => import('components/authentication/AuthenticationFooter'));
const LanguageSwitcherWrap = lazy(() => import('components/elements/LanguageSwitcherWrap'));

const RedeemInvite = () => {
	const theme = useTheme();

	const styles = {
		...authenticationPagesStyles(theme),
		belowAirControl: {
			color: $white,
			marginTop: '40px',
			marginBottom: '40px',
			[theme.breakpoints.down('lg')]: {
				textAlign: 'center'
			}
		},
		joinAccountButton: {
			height: 50,
			'&.Mui-disabled': {
				backgroundColor: `${$loginLightGray} !important`,
				color: $loginDarkGray
			}
		}
	};
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [invite, setInvite] = useState(null);
	const [redeemed, setRedeemed] = useState(false);
	const { pathname } = useLocation();
	const inviteHash = getLastItemInUrlPath(pathname);
	const [showLoader, setShowLoader] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [passwordFieldFocused, setPasswordFieldFocused] = useState(false);
	const userHasId = invite?.user_id;

	const {
		formState: { errors },
		control,
		handleSubmit,
		getValues,
		watch,
		trigger
	} = useForm({
		defaultValues: {
			password: '',
			confirmPassword: ''
		}
	});

	const passwordCurrentValue = watch('password');
	const confirmPasswordCurrentValue = watch('confirmPassword');

	const passwordsMatch = () => {
		const { password, confirmPassword } = getValues();
		return password && confirmPassword && password === confirmPassword;
	};

	const hasErrors = objectHasKeys(errors);

	const handleClickShowPassword = () => {
		setShowPassword(!showPassword);
	};

	const handleClickShowConfirmPassword = () => {
		setShowConfirmPassword(!showConfirmPassword);
	};

	const handleRedeemInvite = async () => {
		setShowLoader(true);
		if (userHasId) {
			//User is already in db but not on this account
			const withUserRes = await dispatch(redeemInviteWithUser(inviteHash, invite));
			if (withUserRes) {
				setRedeemed(true);
			}
		} else {
			//User is not in db
			const { password, confirmPassword } = getValues();
			const withoutUserRes = await dispatch(
				redeemInviteWithoutUser(inviteHash, {
					password,
					password_confirmation: confirmPassword
				})
			);
			if (withoutUserRes) {
				setRedeemed(true);
			}
		}
		setShowLoader(false);
	};

	const handleClickLogin = () => {
		navigate(`/login`);
	};

	useEffect(() => {
		const checkInvite = async () => {
			const res = await dispatch(redeemCheck(inviteHash));
			if (res.status === 200) {
				//Invite exists and has not been claimed
				setInvite(res.data.data);
			}
			if (res.status === 409) {
				//Invite has already been claimed
				setRedeemed(true);
			}
		};
		checkInvite();
	}, [dispatch, inviteHash]);

	useEffect(() => {
		const validatePasswords = async () => {
			await trigger(['password', 'confirmPassword']);
		};
		if (passwordCurrentValue || confirmPasswordCurrentValue) {
			validatePasswords();
		}
	}, [passwordCurrentValue, confirmPasswordCurrentValue, trigger]);

	return (
		<Suspense fallback={null}>
			<LanguageSwitcherWrap>
				<Box component={'form'} sx={styles.main} onSubmit={handleSubmit(handleRedeemInvite)}>
					<Box sx={styles.headerContainer}>
						<Box
							component={'img'}
							src={AloftLogo}
							alt=''
							onClick={handleClickLogin}
							sx={styles.logo}
						/>
					</Box>
					<Grid container sx={styles.mainContainer}>
						<Grid item xs={12} sm={12} md={12} lg={6} sx={styles.leftContainerText}>
							<Box sx={styles.airControlContainer}>
								<Box sx={styles.signIn}>Welcome to</Box>
								<Box component={'img'} src={AirControl} alt='' sx={styles.airControlLogo} />
								{invite && (
									<Box sx={styles.belowAirControl}>
										<Box sx={styles.inviteName}>
											{t('Hi')} {invite.firstname} {invite.lastname}!
										</Box>
										<Box>
											{t('You have been invited to join the')}
											<Box component={'span'} sx={styles.accountName}>
												{invite.account.account_name}
											</Box>{' '}
											{t('account')}
										</Box>
									</Box>
								)}
							</Box>
						</Grid>
						<Grid item xs={12} sm={12} md={12} lg={6} sx={styles.rightContainer}>
							<Box elevation={0} sx={styles.outlineContainer}>
								{invite && !redeemed && userHasId && (
									<>
										<Box sx={styles.formTitle}>
											{t('Join Account')}
											{showLoader && <LinearProgress />}
										</Box>
										<Button
											color='primary'
											sx={styles.joinAccountButton}
											variant='contained'
											id='signUp'
											type='submit'
											disabled={showLoader || hasErrors}
										>
											{t('Accept Invitation')}
										</Button>
									</>
								)}
								{invite && !redeemed && !userHasId && (
									<>
										<Box sx={styles.formTitle}>
											{t('Create Password')}
											{showLoader && <LinearProgress />}
										</Box>
										<Controller
											render={({ field }) => (
												<TextField
													{...field}
													onFocus={() => setPasswordFieldFocused(true)}
													placeholder={t('Password')}
													inputProps={{ type: showPassword ? 'text' : 'password' }}
													variant='outlined'
													disabled={showLoader}
													sx={styles.textFieldNoMargin}
													InputProps={{
														endAdornment: (
															<InputAdornment position='end'>
																<IconButton onClick={handleClickShowPassword} tabIndex='-1'>
																	{showPassword ? <Visibility /> : <VisibilityOff />}
																</IconButton>
															</InputAdornment>
														)
													}}
												/>
											)}
											name='password'
											control={control}
											rules={{
												required: true,
												validate: {
													passwordShape: validatePasswordShape,
													passwordMismatch: passwordsMatch
												}
											}}
										/>
										{(passwordFieldFocused || errors.password) && (
											<Suspense fallback={null}>
												<PasswordRequirementsChecklist value={passwordCurrentValue} />
											</Suspense>
										)}
										<Controller
											render={({ field }) => (
												<TextField
													{...field}
													placeholder={t('Confirm Password')}
													inputProps={{ type: showConfirmPassword ? 'text' : 'password' }}
													variant='outlined'
													disabled={showLoader}
													sx={styles.textField}
													InputProps={{
														endAdornment: (
															<InputAdornment position='end'>
																<IconButton onClick={handleClickShowConfirmPassword} tabIndex='-1'>
																	{showConfirmPassword ? <Visibility /> : <VisibilityOff />}
																</IconButton>
															</InputAdornment>
														)
													}}
												/>
											)}
											name='confirmPassword'
											control={control}
											rules={{
												required: true,
												validate: {
													passwordMismatch: passwordsMatch
												}
											}}
										/>
										<ErrorMessage
											name='confirmPassword'
											error={errors.confirmPassword}
											sx={styles.error}
										/>
										<Button
											color='primary'
											sx={styles.button}
											variant='contained'
											id='signUp'
											type='submit'
											disabled={showLoader || hasErrors}
										>
											{t('Submit')}
										</Button>
									</>
								)}
								{redeemed && (
									<>
										<Box sx={styles.formTitle}>Get Started</Box>
										<Button
											onClick={handleClickLogin}
											variant='contained'
											color='primary'
											startIcon={<Computer />}
										>
											{t('Sign In')}
										</Button>

										<Box sx={styles.download}>{t('or download the mobile app')}</Box>

										<Box sx={styles.appLinks}>
											<Button
												variant='contained'
												color='primary'
												href={iOSAppLink}
												target='_blank'
												startIcon={<Apple />}
											>
												{t('iPhone')}
											</Button>
											<Button
												variant='contained'
												color='primary'
												href={AndroidAppLink}
												target='_blank'
												startIcon={<Android />}
											>
												{t('Android')}
											</Button>
										</Box>
									</>
								)}
							</Box>
						</Grid>
					</Grid>
					<Suspense fallback={null}>
						<AuthenticationFooter />
					</Suspense>
				</Box>
			</LanguageSwitcherWrap>
		</Suspense>
	);
};

export default RedeemInvite;
